import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import FormField from "@mhj/shared/FormField";
import { apiRequest, setCsrfToken } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";
import type { AuthUser } from "@mhj/shared/types";

export default function Register() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    fullName: string;
    email: string;
    phone: string;
    password: string;
  }>({
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      password: "",
    },
  });
  const registerMutation = useMutation({
    mutationFn: (payload: {
      fullName: string;
      email: string;
      phone?: string | null;
      password: string;
    }) =>
      apiRequest<{ user: AuthUser; csrfToken: string }>("/auth/register", {
        method: "POST",
        data: payload,
      }),
    onSuccess: async (response) => {
      setCsrfToken(response.csrfToken);
      await refresh();
      navigate("/verify-email", { replace: true });
    },
  });

  const onSubmit = async (values: {
    fullName: string;
    email: string;
    phone: string;
    password: string;
  }) => {
    try {
      await registerMutation.mutateAsync({
        fullName: values.fullName,
        email: values.email,
        phone: values.phone || null,
        password: values.password,
      });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sky-50 px-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="glass-card w-full max-w-lg rounded-3xl p-8"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Apply for scholarship
        </p>
        <h1 className="mt-3 text-2xl font-semibold text-slate-900">
          Create applicant account
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          One account per applicant. Email verification required.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <FormField label="Full name">
            <input
              type="text"
              {...register("fullName", { required: "Full name is required" })}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="Mu'azu Jantabo"
            />
          </FormField>
          <FormField label="Phone (optional)">
            <input
              type="tel"
              {...register("phone")}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="+234..."
            />
          </FormField>
        </div>
        {errors.fullName ? (
          <p className="text-xs text-red-200">{errors.fullName.message}</p>
        ) : null}
        <div className="mt-4 flex flex-col gap-4">
          <FormField label="Email">
            <input
              type="email"
              {...register("email", { required: "Email is required" })}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="you@example.com"
            />
          </FormField>
          <FormField label="Password">
            <input
              type="password"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="At least 8 characters"
            />
          </FormField>
        </div>
        {errors.email || errors.password ? (
          <p className="text-xs text-red-200">
            {errors.email?.message || errors.password?.message}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={registerMutation.isPending}
          className="mt-6 w-full rounded-full bg-blue-600 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-blue-200"
        >
          {registerMutation.isPending ? "Creating account..." : "Create account"}
        </button>
        <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
          <span>Already have an account?</span>
          <Link to="/login" className="text-blue-600 hover:text-blue-700">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
